import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronRight, Upload, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import ChatbotDemo from '../components/ChatbotDemo';
import ComparisonSlider from '../components/ComparisonSlider';
import '../styles/LandingPage.css';

gsap.registerPlugin(ScrollTrigger);

const LandingPage = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const blueprintRef = useRef(null);
  const transform2DRef = useRef(null);
  const transform3DRef = useRef(null);
  const videoSectionRef = useRef(null);
  const videoRef = useRef(null);
  const finalCTARef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    // Scroll to top on page load/refresh
    window.scrollTo(0, 0);

    // Hero section - Blueprint line drawing animation
    gsap.fromTo(
      blueprintRef.current,
      { opacity: 0, scale: 0.95 },
      {
        opacity: 1,
        scale: 1,
        duration: 2,
        ease: 'power2.out'
      }
    );

    // Scroll Phase 1 - 2D to 3D tilt
    gsap.to(transform2DRef.current, {
      scrollTrigger: {
        trigger: transform2DRef.current,
        start: 'top center',
        end: 'bottom center',
        scrub: 1,
        onUpdate: (self) => setScrollProgress(self.progress)
      },
      rotateX: -35,
      rotateY: 15,
      scale: 0.9,
      z: -200,
      ease: 'power2.inOut'
    });

    // Scroll Phase 2 - 3D floorplan emergence
    gsap.fromTo(
      transform3DRef.current,
      { opacity: 0, scale: 0.8, y: 100 },
      {
        scrollTrigger: {
          trigger: transform3DRef.current,
          start: 'top 80%',
          end: 'top 30%',
          scrub: 1
        },
        opacity: 1,
        scale: 1,
        y: 0,
        ease: 'power2.out'
      }
    );

    // Video section - Play video on scroll
    if (videoRef.current) {
      ScrollTrigger.create({
        trigger: videoSectionRef.current,
        start: 'top center',
        end: 'bottom center',
        onEnter: () => {
          if (videoRef.current) {
            videoRef.current.play().catch(() => {});
          }
        },
        onLeave: () => {
          if (videoRef.current) {
            videoRef.current.pause();
          }
        },
        onEnterBack: () => {
          if (videoRef.current) {
            videoRef.current.play().catch(() => {});
          }
        },
        onLeaveBack: () => {
          if (videoRef.current) {
            videoRef.current.pause();
          }
        }
      });
    }

    // Final CTA - Subtle orbit animation
    gsap.to(finalCTARef.current, {
      scrollTrigger: {
        trigger: finalCTARef.current,
        start: 'top center',
        end: 'bottom center',
        scrub: 1
      },
      rotateY: 360,
      ease: 'none'
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  const handleGetStarted = () => {
    navigate('/login');
  };

  return (
    <div className="landing-page">
      {/* Navigation Header */}
      <header className="dark-header">
        <div className="logo-container">
          <img
            src="https://customer-assets.emergentagent.com/job_5fec953c-1553-4442-b50d-48094837ff2f/artifacts/yv8qmlbd_AI_Architecture_Logo__2_-removebg-preview.png"
            alt="Ezai Build"
            className="logo-icon"
            style={{ width: 44, height: 44 }}
          />
          <span className="logo-text">Ezai Build</span>
        </div>
        <nav className="dark-nav">
          <a href="#features" className="dark-nav-link">Features</a>
          <a href="#demo" className="dark-nav-link">Demo</a>
          <a href="#pricing" className="dark-nav-link">Pricing</a>
          <Button className="btn-primary" onClick={() => navigate('/login')}>
            Get Started
            <ChevronRight size={20} />
          </Button>
        </nav>
      </header>

      {/* Hero Section - Video */}
      <section ref={heroRef} className="hero-section video-hero">
        <div className="hero-video-container">
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            loop
            muted
            playsInline
            preload="auto">

            <source
              src="/hero_video.mp4"
              type="video/mp4" />

          </video>
          <div className="hero-video-overlay"></div>
        </div>
        <div className="hero-content-overlay">
          <h1 className="display-huge hero-headline">
            Turn your drawings
            <br />
            <span className="accent-text">into reality</span>
          </h1>
          <p className="body-large hero-subtitle">
            Upload 2D building plans and watch AI transform them into
            <br />
            stunning, photorealistic 3D visualizations in seconds.
          </p>
          <div className="hero-cta-group">
            <Button className="btn-primary btn-large" onClick={handleGetStarted}>
              Start Creating
              <ChevronRight size={24} />
            </Button>
            <Button className="btn-secondary btn-large" onClick={handleGetStarted}>
              <Upload size={20} />
              Upload Drawing
            </Button>
          </div>
        </div>
      </section>

      {/* Chatbot Demo Section */}
      <section className="chatbot-section" id="demo">
        <div className="section-content">
          <h2 className="display-large section-title">AI-Powered Workflow</h2>
          <p className="body-medium section-subtitle">
            Simply upload your drawing and describe what you want to see
          </p>
        </div>
        <ChatbotDemo />
      </section>

      {/* Before vs After Comparison */}
      <section className="comparison-section">
        <div className="section-content">
          <h2 className="display-large section-title">The Transformation</h2>
          <p className="body-medium section-subtitle">
            Drag the slider to reveal the journey from concept to creation
          </p>
        </div>
        <ComparisonSlider
          beforeImage="/floorplan_3d.png"
          afterImage="/blueprint.png" />

      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div ref={finalCTARef} className="cta-3d-container">
          <img
            src="/frontview.png"
            alt="Final 3D Render"
            className="cta-image" />

        </div>
        <div className="cta-content">
          <h2 className="display-huge cta-headline">
            Ready to transform
            <br />
            your vision?
          </h2>
          <p className="body-large cta-subtitle">
            Join architects and developers creating the future
          </p>
          <Button className="btn-primary btn-large" onClick={handleGetStarted}>
            Turn your drawing into reality
            <ChevronRight size={24} />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="dark-footer">
        <div className="footer-content">
          <div className="footer-section">
            <div className="logo-container">
              <img
                src="https://customer-assets.emergentagent.com/job_5fec953c-1553-4442-b50d-48094837ff2f/artifacts/yv8qmlbd_AI_Architecture_Logo__2_-removebg-preview.png"
                alt="Ezai Build"
                className="logo-icon"
                style={{ width: 36, height: 36 }}
              />
              <span className="logo-text">Ezai Build</span>
            </div>
            <p className="body-small footer-description">
              AI-powered 2D to 3D transformation for architects and developers
            </p>
          </div>
          <div className="footer-links">
            <div className="footer-column">
              <h4 className="heading-3">Product</h4>
              <a href="#features" className="footer-link">Features</a>
              <a href="#pricing" className="footer-link">Pricing</a>
              <a href="#demo" className="footer-link">Demo</a>
            </div>
            <div className="footer-column">
              <h4 className="heading-3">Company</h4>
              <a href="#about" className="footer-link">About</a>
              <a href="#contact" className="footer-link">Contact</a>
              <a href="#careers" className="footer-link">Careers</a>
            </div>
            <div className="footer-column">
              <h4 className="heading-3">Legal</h4>
              <a href="#privacy" className="footer-link">Privacy</a>
              <a href="#terms" className="footer-link">Terms</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p className="body-small">© 2026 Ezai Build. All rights reserved.</p>
        </div>
      </footer>
    </div>);

};

export default LandingPage;